import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import posthog from "posthog-js";
import { PostHogProvider as PHProvider } from "posthog-js/react";

const POSTHOG_PROJECT_TOKEN = process.env.REACT_APP_POSTHOG_PROJECT_TOKEN;
const POSTHOG_API_HOST =
  process.env.REACT_APP_POSTHOG_API_HOST ?? "https://eu.i.posthog.com";
const POSTHOG_ENVIRONMENT =
  process.env.REACT_APP_POSTHOG_ENVIRONMENT ?? "unknown";

const isConfigured =
  !!POSTHOG_PROJECT_TOKEN &&
  POSTHOG_ENVIRONMENT.trim().toLowerCase() === "production";

let lastCapturedPageKey: string | undefined;

/** @internal Exposed for test cleanup only. */
export function resetPageViewTracking() {
  lastCapturedPageKey = undefined;
}

if (isConfigured) {
  posthog.init(POSTHOG_PROJECT_TOKEN, {
    api_host: POSTHOG_API_HOST,
    capture_pageview: false, // handled manually for SPA route changes
    capture_pageleave: true,
    autocapture: true,
    persistence: "localStorage+cookie",
    mask_all_text: true,
    mask_all_element_attributes: true,
    cross_subdomain_cookie: false, // we don't need to share cookies across subdomains

    debug: process.env.NODE_ENV === "development", // only log events to console in development
  });

  posthog.register({ environment: POSTHOG_ENVIRONMENT });
}

function PageViewTracker() {
  const location = useLocation();

  useEffect(() => {
    if (isConfigured) {
      const currentKey = `${location.pathname}${location.search}`;
      if (currentKey !== lastCapturedPageKey) {
        lastCapturedPageKey = currentKey;
        posthog.capture("$pageview");
      }
    }
  }, [location.pathname, location.search]);

  return null;
}

interface PostHogProviderProps {
  children: React.ReactNode;
}

export default function PostHogProviderWrapper({ children }: Readonly<PostHogProviderProps>) {
  if (!isConfigured) {
    return <>{children}</>;
  }

  return (
    <PHProvider client={posthog}>
      <PageViewTracker />
      {children}
    </PHProvider>
  );
}
